import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import CallAnalysisSection from '../components/CallAnalysisSection';
import { StatusBadge } from '../components/ui/status-badge';
import { IntentBadge, NextActionBadge } from '../components/ui/intent-badges';
import { getIntent, getNextAction } from '../lib/analysis-utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  Phone,
  Trash2,
  Loader2,
  Play,
  Pause,
  Volume2,
  FileText,
  Clock,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
  Brain,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignCalls, setCampaignCalls] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [initiatingCall, setInitiatingCall] = useState(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [voiceAgents, setVoiceAgents] = useState([]);

  const [newCampaign, setNewCampaign] = useState({
    name: '',
    phone_number: '',
    analysis_prompt: '',
    voice_agent_id: ''
  });

  useEffect(() => {
    fetchCampaigns();
    fetchVoiceAgents();
  }, []);

  const fetchVoiceAgents = async () => {
    try {
      const response = await axios.get(`${API}/voice-agents`);
      setVoiceAgents(response.data);
    } catch (error) {
      // silently fail
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to fetch campaigns');
    } finally {
      setLoading(false);
    }
  };

  const createCampaign = async (e) => {
    e.preventDefault();
    const payload = { ...newCampaign };
    try {
      const response = await axios.post(`${API}/campaigns`, payload);
      setCampaigns([response.data, ...campaigns]);
      setNewCampaign({ name: '', phone_number: '', analysis_prompt: '', voice_agent_id: '' });
      setCreateDialogOpen(false);
      toast.success('Campaign created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create campaign');
    }
  };

  const deleteCampaign = async (id) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      setCampaigns(campaigns.filter(c => c.id !== id));
      toast.success('Campaign deleted');
    } catch (error) {
      toast.error('Failed to delete campaign');
    }
  };

  const fetchCampaignCalls = async (campaignId) => {
    setLoadingCalls(true);
    try {
      const response = await axios.get(`${API}/campaigns/${campaignId}/calls`);
      setCampaignCalls(response.data);
    } catch (error) {
      toast.error('Failed to fetch calls');
    } finally {
      setLoadingCalls(false);
    }
  };

  const initiateCall = async (campaignId) => {
    setInitiatingCall(campaignId);
    try {
      const response = await axios.post(`${API}/campaigns/${campaignId}/initiate-call`);
      toast.success('Call initiated! Check back for status updates.');
      // Refresh calls list for this campaign only
      if (expandedCampaigns[campaignId]) {
        fetchCampaignCalls(campaignId);
      }
      // Update call count locally instead of refetching all campaigns
      setCampaigns(prev => prev.map(c =>
        c.id === campaignId ? { ...c, call_count: (c.call_count || 0) + 1 } : c
      ));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate call');
    } finally {
      setInitiatingCall(null);
    }
  };

  const toggleCampaignExpand = (campaignId) => {
    const isExpanding = !expandedCampaigns[campaignId];
    setExpandedCampaigns(prev => ({
      ...prev,
      [campaignId]: isExpanding
    }));
    if (isExpanding) {
      setSelectedCampaign(campaigns.find(c => c.id === campaignId));
      fetchCampaignCalls(campaignId);
    }
  };

  const openCallDetails = (call) => {
    setSelectedCall(call);
    setCallDialogOpen(true);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-48 bg-gray-100" />
          <Skeleton className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="campaigns-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">Campaigns</h1>
            <p className="text-gray-500 mt-1">Manage your real estate outbound call campaigns</p>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="create-campaign-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Campaign
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="font-heading text-gray-900">Create New Campaign</DialogTitle>
                <DialogDescription className="text-gray-500">
                  Set up a new real estate outbound call campaign with AI voice agent
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={createCampaign} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Campaign Name</Label>
                  <Input
                    data-testid="campaign-name-input"
                    placeholder="e.g., Skyline Towers - Phase 2 Launch"
                    value={newCampaign.name}
                    onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Phone Number</Label>
                  <Input
                    data-testid="campaign-phone-input"
                    placeholder="e.g., +1234567890"
                    value={newCampaign.phone_number}
                    onChange={(e) => setNewCampaign({ ...newCampaign, phone_number: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Voice Agent</Label>
                  <select
                    value={newCampaign.voice_agent_id}
                    onChange={(e) => setNewCampaign({ ...newCampaign, voice_agent_id: e.target.value })}
                    className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900"
                    required
                  >
                    <option value="">Select a voice agent...</option>
                    {voiceAgents.filter(a => a.is_active).map(agent => (
                      <option key={agent.id} value={agent.id}>{agent.name}</option>
                    ))}
                  </select>
                  {voiceAgents.length === 0 && (
                    <p className="text-xs text-amber-600">No voice agents yet. Create one in the Voice Agents page first.</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-gray-700">Analysis Prompt (Optional)</Label>
                  <p className="text-xs text-gray-500">
                    Define how AI should analyze completed call transcripts. Leave empty to use agent's analysis prompt or default analysis.
                  </p>
                  <Textarea
                    data-testid="campaign-analysis-prompt-input"
                    placeholder="e.g., Analyze this real estate cold call. Return JSON with: sentiment, interest_level, intent, next_action, key_objections, summary..."
                    value={newCampaign.analysis_prompt}
                    onChange={(e) => setNewCampaign({ ...newCampaign, analysis_prompt: e.target.value })}
                    className="bg-white border-gray-300 text-gray-900 min-h-[80px]"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-campaign-btn">
                    Create Campaign
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Campaigns List */}
        {campaigns.length === 0 ? (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="py-16 text-center">
              <Phone className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">No campaigns yet. Create your first campaign to start making calls!</p>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Campaign
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <Card 
                key={campaign.id} 
                className="bg-white border-gray-200 shadow-sm"
                data-testid={`campaign-card-${campaign.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <CardTitle className="font-heading text-lg text-gray-900">{campaign.name}</CardTitle>
                        <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300">
                          {campaign.call_count} calls
                        </Badge>
                      </div>
                      <CardDescription className="mt-1">
                        <span className="font-mono text-blue-400">{campaign.phone_number}</span>
                        {campaign.voice_agent_name && (
                          <>
                            <span className="text-gray-500 mx-2">•</span>
                            <span className="text-blue-500 text-xs font-medium">Agent: {campaign.voice_agent_name}</span>
                          </>
                        )}
                        <span className="text-gray-500 mx-2">•</span>
                        <span className="text-gray-500">Created {format(new Date(campaign.created_at), 'MMM d, yyyy')}</span>
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => initiateCall(campaign.id)}
                        disabled={initiatingCall === campaign.id}
                        data-testid={`initiate-call-${campaign.id}-btn`}
                      >
                        {initiatingCall === campaign.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Phone className="w-4 h-4 mr-1" />
                            Call
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-500 hover:text-rose-400"
                        onClick={() => deleteCampaign(campaign.id)}
                        data-testid={`delete-campaign-${campaign.id}-btn`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-500"
                        onClick={() => toggleCampaignExpand(campaign.id)}
                        data-testid={`expand-campaign-${campaign.id}-btn`}
                      >
                        {expandedCampaigns[campaign.id] ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                {/* Expanded Content - Calls List */}
                {expandedCampaigns[campaign.id] && (
                  <CardContent className="pt-0">
                    <Separator className="my-4 bg-gray-200" />
                    
                    {/* Prompt Preview */}
                    <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">AI Prompt</p>
                      <p className="text-sm text-gray-700 line-clamp-3">{campaign.prompt}</p>
                    </div>
                    {campaign.analysis_prompt && (
                      <div className="mb-4 p-3 rounded-lg bg-gray-50 border border-gray-200">
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Analysis Prompt</p>
                        <p className="text-sm text-gray-700 line-clamp-2">{campaign.analysis_prompt}</p>
                      </div>
                    )}
                    
                    {/* Calls */}
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-medium text-gray-500">Call History</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-gray-500 hover:text-gray-900"
                        onClick={() => fetchCampaignCalls(campaign.id)}
                        data-testid={`refresh-calls-${campaign.id}-btn`}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Refresh
                      </Button>
                    </div>
                    
                    {loadingCalls ? (
                      <div className="space-y-2">
                        <Skeleton className="h-12 bg-gray-100" />
                        <Skeleton className="h-12 bg-gray-100" />
                      </div>
                    ) : campaignCalls.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center py-4">No calls yet for this campaign</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow className="border-gray-300 hover:bg-transparent">
                            <TableHead className="text-gray-500 text-xs">Phone</TableHead>
                            <TableHead className="text-gray-500 text-xs">Status</TableHead>
                            <TableHead className="text-gray-500 text-xs">Intent</TableHead>
                            <TableHead className="text-gray-500 text-xs">Next Action</TableHead>
                            <TableHead className="text-gray-500 text-xs">Time</TableHead>
                            <TableHead className="text-gray-500 text-xs text-right">Details</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {campaignCalls.map((call) => (
                            <TableRow 
                              key={call.id} 
                              className="border-gray-300 hover:bg-gray-50 cursor-pointer"
                              onClick={() => openCallDetails(call)}
                              data-testid={`call-row-${call.id}`}
                            >
                              <TableCell className="font-mono text-sm text-gray-700">{call.phone_number}</TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <StatusBadge status={call.status} />
                                  {call.analysis_status === 'completed' && (
                                    <Brain className="w-3.5 h-3.5 text-blue-400" title="Analysis available" />
                                  )}
                                  {call.analysis_status === 'running' && (
                                    <Loader2 className="w-3.5 h-3.5 text-blue-400 animate-spin" title="Analyzing..." />
                                  )}
                                  {call.analysis_status === 'failed' && (
                                    <AlertCircle className="w-3.5 h-3.5 text-rose-400" title="Analysis failed" />
                                  )}
                                </div>
                              </TableCell>
                              <TableCell><IntentBadge intent={getIntent(call.analysis_result)} /></TableCell>
                              <TableCell><NextActionBadge action={getNextAction(call.analysis_result)} /></TableCell>
                              <TableCell className="text-gray-500 text-xs">
                                {format(new Date(call.created_at), 'MMM d, HH:mm')}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-gray-500 hover:text-gray-900"
                                  data-testid={`view-call-details-${call.id}-btn`}
                                >
                                  View
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Call Details Dialog */}
        <CallDetailsDialog
          call={selectedCall}
          open={callDialogOpen}
          onOpenChange={setCallDialogOpen}
          onReanalyze={async (callId) => {
            try {
              await axios.post(`${API}/calls/${callId}/analyze`);
              toast.success('Analysis triggered. Refresh in a few seconds to see results.');
            } catch (error) {
              toast.error(error.response?.data?.detail || 'Failed to trigger analysis');
            }
          }}
        />
      </div>
    </DashboardLayout>
  );
}

function CallDetailsDialog({ call, open, onOpenChange, onReanalyze }) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);

  if (!call) return null;

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="font-heading text-gray-900">Call Details</DialogTitle>
            <StatusBadge status={call.status} />
          </div>
          <DialogDescription className="text-gray-500">
            <span className="font-mono">{call.phone_number}</span>
            <span className="mx-2">•</span>
            {format(new Date(call.created_at), 'MMMM d, yyyy HH:mm')}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6">
            {/* Audio Player */}
            {call.recording_url && (
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <Volume2 className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Recording</span>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-gray-300"
                    onClick={togglePlay}
                    data-testid="play-recording-btn"
                  >
                    {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                  </Button>
                  <audio
                    ref={audioRef}
                    src={call.recording_url}
                    onEnded={() => setIsPlaying(false)}
                    className="flex-1"
                    controls
                  />
                </div>
                <a 
                  href={call.recording_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 mt-2"
                >
                  Open in new tab <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {/* Transcript */}
            {(call.transcript_text || call.transcript_url) && (
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Transcript</span>
                </div>
                {call.transcript_text ? (
                  <div className="font-mono text-sm text-gray-700 whitespace-pre-wrap bg-white p-3 rounded-lg max-h-48 overflow-auto">
                    {call.transcript_text}
                  </div>
                ) : call.transcript_url ? (
                  <a 
                    href={call.transcript_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300"
                  >
                    View transcript <ExternalLink className="w-3 h-3" />
                  </a>
                ) : null}
              </div>
            )}

            {/* Call Analysis */}
            <CallAnalysisSection
              call={call}
              onReanalyze={onReanalyze ? () => onReanalyze(call.id) : undefined}
            />

            {/* Gathered Context */}
            {call.gathered_context && Object.keys(call.gathered_context).length > 0 && (
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Call Context</span>
                </div>
                <div className="font-mono text-xs text-gray-500 bg-white p-3 rounded-lg overflow-auto max-h-48">
                  <pre>{JSON.stringify(call.gathered_context, null, 2)}</pre>
                </div>
              </div>
            )}

            {/* Cost Info */}
            {call.cost_info && Object.keys(call.cost_info).length > 0 && (
              <div className="p-4 rounded-lg bg-gray-50 border border-gray-200">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm font-medium text-gray-700">Cost Information</span>
                </div>
                <div className="font-mono text-xs text-gray-500 bg-white p-3 rounded-lg overflow-auto max-h-32">
                  <pre>{JSON.stringify(call.cost_info, null, 2)}</pre>
                </div>
              </div>
            )}

            {/* No additional data */}
            {!call.recording_url && !call.transcript_text && !call.transcript_url && 
             (!call.gathered_context || Object.keys(call.gathered_context).length === 0) && (
              <div className="text-center py-8">
                <Clock className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">
                  {call.status === 'completed' 
                    ? 'Call completed. Additional details may be available soon.'
                    : 'Call details will appear here once the call is completed.'}
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
